import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { Add, Star } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
  deDE,
} from '@mui/x-data-grid';
import { useFederalStates } from '../../Aemter/hooks';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { ILocation, locationSchema } from '../../Interfaces/ILocation';
import { useGetLocations } from '../../hooks/reactQuerys/LocationQueries';
import { useGetRooms } from '../../hooks/reactQuerys/RoomQueries';
import { LocationEdit } from './LocationEdit';

type LocationRow = ILocation & { _roomCount: number };

const columns: GridColDef[] = [
  { field: 'idLocation', headerName: 'Nr.', width: 80 },
  {
    field: 'isDefault',
    headerName: 'Standard',
    width: 120,
    renderCell: ({ row }: GridRenderCellParams<LocationRow>) =>
      row.isDefault ? (
        <Chip
          size='small'
          color='warning'
          icon={<Star fontSize='small' />}
          label='Standard'
        />
      ) : null,
  },
  { field: 'Location', headerName: 'Standort', flex: 1, minWidth: 160 },
  {
    field: '_roomCount',
    headerName: 'Räume',
    width: 90,
    type: 'number',
  },
  { field: 'FederalState', headerName: 'Bundesland', width: 160 },
  { field: 'Postcode', headerName: 'PLZ', width: 100 },
  { field: 'City', headerName: 'Ort', width: 160 },
  {
    field: 'address',
    headerName: 'Adresse',
    flex: 1,
    minWidth: 200,
    sortable: false,
    valueGetter: ({ row }: GridValueGetterParams<LocationRow>) =>
      [row.Street, row.StreetNr, row.StreetAdditional]
        .filter(value => value && String(value).trim() !== '')
        .join(' '),
  },
];

const getEmptyLocation = (): ILocation =>
  locationSchema.parse({
    idLocation: -1,
    idFederalState: 0,
    idPostcode: null,
  });

export const LocationOverview: React.FC = () => {
  const { locations, isLoadingLocations } = useGetLocations();
  const { rooms, isLoadingRooms } = useGetRooms();
  const { federalStates, isLoadingFederalStates } = useFederalStates();

  const [editLocation, setEditLocation] = useState<ILocation | null>(null);

  const roomCountByLocation = useMemo(() => {
    const map = new Map<number, number>();
    for (const room of rooms) {
      map.set(
        room.Location_idLocation,
        (map.get(room.Location_idLocation) ?? 0) + 1
      );
    }
    return map;
  }, [rooms]);

  const locationRows = useMemo<LocationRow[]>(() => {
    if (!locations) return [];
    return locations.map(location => {
      const parsed = locationSchema.parse(location);
      return {
        ...parsed,
        _roomCount: roomCountByLocation.get(parsed.idLocation) ?? 0,
      };
    });
  }, [locations, roomCountByLocation]);

  if (isLoadingLocations || isLoadingFederalStates || isLoadingRooms) {
    return <LoadingSpinner />;
  }

  if (!locations || !federalStates) {
    return <Alert severity='error'>Fehler beim Laden der Standorte.</Alert>;
  }

  return (
    <>
      <Dialog
        open={editLocation !== null}
        onClose={() => setEditLocation(null)}
        maxWidth='md'
        fullWidth
      >
        <DialogContent>
          {editLocation && (
            <LocationEdit
              currentLocation={editLocation}
              federalStates={federalStates}
              onClose={() => setEditLocation(null)}
              onSaved={() => setEditLocation(null)}
              onDeleted={() => setEditLocation(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ m: 2, mt: 0 }}>
        <Typography variant='h4'>
          Standorte
          <IconButton
            sx={{ float: 'right' }}
            onClick={() => setEditLocation(getEmptyLocation())}
          >
            <Add />
          </IconButton>
        </Typography>

        <DataGrid
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={locationRows}
          columns={columns}
          getRowId={row => row.idLocation}
          autoHeight
          onRowClick={params =>
            setEditLocation(locationSchema.parse(params.row))
          }
        />
      </Box>
    </>
  );
};
